import { lowerCase, map as mapCapped, replace } from 'lodash/fp';
import dayjs from 'dayjs';
import React from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import {
  HomeTwoTone,
  LocalPhoneTwoTone,
  PermPhoneMsgTwoTone,
} from '@mui/icons-material';
import { Caption } from './material-theme';
import { StyledButtonGrid } from './assests/style';
import { AddToCalendar } from './AddToCalendarButton';
import { SignUpButton, WithdrawButton } from './SignupButton';
import { toLocaleDate, toLocaleDateShort } from './util/to-local-date';
import { TemporaryHousingDoneButton } from './TemporaryHousingDoneButton';

const map = mapCapped.convert({ cap: false });

const requestTitle = 'Temporary Housing';

const TemporaryHousing = ({
  request: {
    Id,
    Date_Begins__c,
    Move_Out_Date__c,
    Departure_Date__c,
    Event__r,
    Form_Submission__c,
    Contact,
    signUpInfo: {
      isSignedUp,
      canSignUpTemporaryHousing,
      isSignedUpTemporaryHousing,
      isHistoryRecord,
      isPendingApproval,
    },
    Host_Family_account__r,
    Host_Family_Phone__c,
  },
  actionPerform,
  requestType,
}) => {
  const isEvent = requestType === 'Event';

  const setPayloadForCalendar = () => {
    return {
      summary: `${Contact?.Name} ${requestTitle}`,
      description: `View in IFI Volunteer App.
      ${window.location.href}#_${Id}`,
      //location: 'location',
      startDate: dayjs(Date_Begins__c).format('YYYYMMDDTHHmmss'),
      endDate: dayjs(Move_Out_Date__c).format('YYYYMMDDTHHmmss'),
      dstamp: dayjs().format('YYYYMMDDTHHmmss'),
    };
  };

  const tempHousingAction =
    (actionUrl, isSignUpRequest = false) =>
    (payload) => {
      const requestName = isEvent ? 'events' : 'requests';

      const apiUrl = `/api/${requestName}/temporary-housing/${actionUrl}`;

      const params = { requestId: Id, ...(payload || {}) };
      if (isEvent) {
        params.eventId = Event__r.Id;
      }

      return actionPerform(apiUrl, params, isSignUpRequest);
    };

  const layout = [
    {
      caption: 'From',
      body: toLocaleDate(Date_Begins__c),
    },
    {
      caption: 'To',
      body: toLocaleDate(Move_Out_Date__c),
    },
  ];

  if (isSignedUp && !canSignUpTemporaryHousing && !isPendingApproval) {
    layout.push({
      caption: 'Host Family',
      body: Host_Family_account__r?.Name,
      customContent:
        isSignedUp &&
        !isSignedUpTemporaryHousing &&
        (Host_Family_Phone__c ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              target="blank"
              href={`tel:${Host_Family_Phone__c}`}
              endIcon={<LocalPhoneTwoTone />}
            >
              Call
            </Button>
            <Button
              variant="outlined"
              size="small"
              target="blank"
              href={`sms:${Host_Family_Phone__c}`}
              endIcon={<PermPhoneMsgTwoTone />}
            >
              Text
            </Button>
          </Stack>
        ) : (
          <Typography variant="caption">
            Phone number is not available
          </Typography>
        )),
      spaceCount: { xs: 12 },
    });
  }

  const getActionButtons = () => {
    const buttons = [];
    const confirmHeader = `${requestTitle} ${toLocaleDateShort(
      Date_Begins__c,
    )} - ${toLocaleDateShort(Move_Out_Date__c)}`;

    if (canSignUpTemporaryHousing && !isHistoryRecord) {
      buttons.push({
        spaceCount: { xs: 12 },
        btn: (
          <SignUpButton
            onClick={tempHousingAction('signup', true)}
            Icon={<HomeTwoTone />}
            confirmHeader={confirmHeader}
          />
        ),
      });
    }
    if (isSignedUpTemporaryHousing && !Departure_Date__c) {
      if (!isPendingApproval) {
        buttons.push({
          spaceCount: { xs: 12, md: 6 },
          btn: (
            <TemporaryHousingDoneButton
              Contact={Contact}
              onClick={(doneDate) =>
                tempHousingAction('done')({
                  doneDate,
                  Form_Submission__c
                })
              }
            />
          ),
        });
      }
      if (!isHistoryRecord) {
        buttons.push({
          spaceCount: { xs: 12, md: 6 },
          btn: (
            <WithdrawButton
              onClick={tempHousingAction(
                'withdraw',
              )}
              confirmHeader={confirmHeader}
              Icon={<HomeTwoTone />}
            />
          ),
        });
      }

      if (!isPendingApproval && !isHistoryRecord) {
        buttons.push({
          spaceCount: { xs: 12 },
          btn: (
            <AddToCalendar
              getPayload={setPayloadForCalendar}
              fileName={`ifi-${replace(
                ' ',
                '-',
                lowerCase(requestTitle),
              )}-${Id}`}
            />
          ),
        });
      }
    }
    return buttons;
  };

  const actionButtons = getActionButtons();

  return (
    !Move_Out_Date__c || (
      <Grid container spacing={0.5}>
        <Grid item xs={12}>
          <Typography variant="h5">
            <HomeTwoTone /> {requestTitle}
          </Typography>
        </Grid>
        {map(
          ({ caption, body, customContent, spaceCount = { xs: 6 } }, index) => (
            <Grid item {...spaceCount} key={index}>
              <Caption>{caption}</Caption>
              <Typography>{body}</Typography>
              {customContent}
            </Grid>
          ),
          layout,
        )}
        {map(
          ({ spaceCount, btn }, index) => (
            <StyledButtonGrid item {...spaceCount} key={index}>
              {btn}
            </StyledButtonGrid>
          ),
          actionButtons,
        )}
      </Grid>
    )
  );
};

export { TemporaryHousing };
